import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { NewsletterService } from '@df/session/newsletter/newsletter.service';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { AbstractFormComponent } from '@df/ui/form/abstract-form.component';
import { ModalService } from '@df/ui/modal/modal.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ProductDecorator } from '../../../catalog/model/product/product.decorator';
import { ActionComponent } from '../../../ui/api/action.component';
import { ResultComponent } from '../../../ui/api/result.component';
import { CheckboxComponent } from '../../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { MuiInputDirective } from '../../../ui/form/mui-input.directive';
import { NBloomreach } from '../bloomreach.namespace';
import { BloomreachService } from '../bloomreach.service';

@Component({
    selector: 'bloomreach-stock-notification',
    templateUrl: './bloomreach-stock-notification.html',
    styleUrl: './bloomreach-stock-notification.scss',
    standalone: true,
    imports: [
        ActionComponent,
        CheckboxComponent,
        CommonModule,
        FormsModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        MuiInputDirective,
        OptionLabelPipe,
        ResultComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BloomreachStockNotificationComponent extends AbstractFormComponent<NBloomreach.IStockNotificationData> {
    isSuccess = false;

    @Input()
    isSimple?: boolean;

    @Input()
    model!: ProductDecorator<IProductDataV3>;

    @Output()
    openSizeTray = new EventEmitter<boolean>();

    @Output()
    closeSizeTray = new EventEmitter<boolean>();

    @Output()
    closeSizeModal = new EventEmitter<boolean>();

    protected submitPath = 'lead_watchdog_stock';

    private consentStatus = new Status();
    private consentResult = false;

    private checkBloomreachConsentDebounced!: (email?: string) => void;

    public client = inject(Client);
    private modalService = inject(ModalService);
    private bloomreachService = inject(BloomreachService);
    private timeoutService = injectTimeoutService();
    private customerModel = inject(CustomerModel);
    private newsletterService = inject(NewsletterService);

    override ngOnInit(): void {
        super.ngOnInit();
        this.checkBloomreachConsentDebounced = this.timeoutService.debounce((email?: string) => this.checkBloomreachConsent(email), 1000);
        this.subscriptions.add(this.consentStatus.subscribe(() => this.detectChanges()));
    }

    override ngOnChanges(changes: SimpleChanges) {
        this.isSuccess = false;
        super.ngOnChanges(changes);
    }

    override submit(): void {
        const data = { ...this.data };
        const product = this.model;
        this.requestService.submitForm(
            this.form,
            this.status,
            () => {
                return new Promise<void>((resolve, reject) => {
                    const email = data.email;
                    if (email && product) {
                        const finalize = (consent: boolean) => {
                            this.bloomreachService.identifyCustomer(this.customerModel.isLoggedIn ? undefined : email, undefined, email);

                            const finish = () => {
                                if (product?.data?.sku) {
                                    this.bloomreachService.sendBackInStockEvent(product.data.sku, this.customerModel.isLoggedIn ? undefined : email);
                                }
                                resolve();
                            };

                            if (data.newsletter && !consent) {
                                this.newsletterService.subscribe(
                                    undefined,
                                    undefined,
                                    { email: email, consent: true, source: 'backinstock' },
                                    finish,
                                    finish
                                );
                            } else {
                                finish();
                            }
                        };
                        this.bloomreachService.checkConsent(
                            undefined,
                            email,
                            result => {
                                finalize(!!result?.consent);
                            },
                            () => {
                                finalize(false);
                            }
                        );
                    } else {
                        reject();
                    }
                });
            },
            response => this.onSubmitSuccess(response)
        );
    }

    toggleSizeTray(): void {
        this.openSizeTray.emit(true);
    }

    closeAllModals(): void {
        this.modalService.closeAll();
    }

    closeModal(): void {
        this.app.events.broadcast('pageSidebar.close');
    }

    onEmailChange(): void {
        this.checkBloomreachConsentDebounced(this.data?.email);
    }

    get showSubscriptionOptIn(): boolean {
        return !this.consentResult;
    }

    protected override onSubmitSuccess(response?: unknown): void {
        this.isSuccess = true;
        super.onSubmitSuccess(response);
    }

    private checkBloomreachConsent(email?: string): void {
        if (email) {
            this.bloomreachService.checkConsent(this.consentStatus, email, status => {
                this.consentResult = status.consent;
                this.detectChanges();
            });
        } else {
            if (this.consentStatus.promise?.cancel) {
                this.consentStatus.promise.cancel();
            }

            this.consentResult = false;
            this.detectChanges();
        }
    }
}
